import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserActions } from 'store/User/Actions';
import { hasValidSubscription, useUser } from 'store/User/Context';
import { purchaseApiRequests } from 'utils/api-requests/purchase';
import { userApiRequests } from 'utils/api-requests/user';
import { initialState, isAccountDataFilled, isInvoiceDataFilled, POLLING_INTERVAL_MS, POLLING_TIMEOUT_MS, SUBSCRIPTION_PRODUCT_ID } from 'utils/state-managment/order/subscriptionPurchase';
import { OrderCreatedDto, OrderCreatedDtoOrderStatusEnum, OrderListDtoOrderStatusEnum, StatusDtoOrderStatusEnum } from 'utils/types/OrderModels';
import { SubscriptionPurchaseStateType } from 'utils/types/Subscription';

import { createPaymentLoadingContent } from 'components/common/Purchase/createPaymentLoadingContent';
import { AccountFormData, hasErrors, InvoiceFormData, validateForm } from 'components/common/Purchase/ProductPurchaseForm';
import { activeNotification } from 'components/functions/activeNotification';

export interface SubscriptionPurchaseContextType {
    subscriptionState: SubscriptionPurchaseStateType;
    handleAccountDataChange: (field: keyof AccountFormData, value: string) => void;
    handleInvoiceDataChange: (field: keyof InvoiceFormData, value: string) => void;
    handleSubmitForm: (e: React.FormEvent) => void;
    handleCancel: () => void;
    clearState: () => void;
    checkPendingOrder: () => Promise<void>;
    handleCheckPaymentStatus: () => Promise<void>;
}

const values: SubscriptionPurchaseContextType = {
    subscriptionState: {} as SubscriptionPurchaseStateType,
    handleAccountDataChange: () => {},
    handleInvoiceDataChange: () => {},
    handleSubmitForm: () => {},
    handleCancel: () => {},
    clearState: () => {},
    checkPendingOrder: async () => {},
    handleCheckPaymentStatus: async () => {}
};

export const SubscriptionPurchaseContext = createContext(values);

export const SubscriptionPurchaseProvider = ({ children }: { children: React.ReactNode }) => {
    const navigate = useNavigate();
    const { dispatch, user } = useUser();
    const [subscriptionState, setSubscriptionState] = useState(initialState.subscriptionPurchaseInitialState());
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isLoggedUser = user.isLogged;

    useEffect(() => {
        const savedInvoiceData = localStorage.getItem('savedInvoiceData');

        if (savedInvoiceData) {
            try {
                const parsedData = JSON.parse(savedInvoiceData);
                setSubscriptionState((prev) => ({
                    ...prev,
                    invoiceData: parsedData
                }));
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (error) {
                localStorage.removeItem('savedInvoiceData');
            }
        }

        const savedAccountData = localStorage.getItem('savedAccountData');

        if (savedAccountData && !isLoggedUser) {
            try {
                const parsedData = JSON.parse(savedAccountData);
                setSubscriptionState((prev) => ({
                    ...prev,
                    accountData: parsedData
                }));
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (error) {
                localStorage.removeItem('savedAccountData');
            }
        }

        if (isLoggedUser && savedAccountData) {
            localStorage.removeItem('savedAccountData');
        }
    }, [isLoggedUser]);

    useEffect(() => {
        if (!isLoggedUser && isAccountDataFilled(subscriptionState.accountData)) {
            localStorage.setItem('savedAccountData', JSON.stringify(subscriptionState.accountData));
        }
    }, [subscriptionState.accountData, isLoggedUser]);

    useEffect(() => {
        if (isInvoiceDataFilled(subscriptionState.invoiceData)) {
            localStorage.setItem('savedInvoiceData', JSON.stringify(subscriptionState.invoiceData));
        }
    }, [subscriptionState.invoiceData]);

    const loadSubscriptionProduct = async () => {
        const product = await purchaseApiRequests.getProductDetails(SUBSCRIPTION_PRODUCT_ID);

        if (product) {
            setSubscriptionState((prev) => ({
                ...prev,
                subscriptionProduct: product
            }));
        }
    };

    useEffect(() => {
        if (!hasValidSubscription(user.userRole)) {
            loadSubscriptionProduct();
        }

        return;
    }, []);

    const handleAccountDataChange = useCallback((field: keyof AccountFormData, value: string) => {
        setSubscriptionState((prev) => ({
            ...prev,
            accountData: {
                ...prev.accountData,
                [field]: value
            },
            errors: {
                ...prev.errors,
                [field]: ''
            }
        }));
    }, []);

    const handleInvoiceDataChange = useCallback((field: keyof InvoiceFormData, value: string) => {
        setSubscriptionState((prev) => ({
            ...prev,
            invoiceData: {
                ...prev.invoiceData,
                [field]: value
            },
            errors: {
                ...prev.errors,
                [field]: ''
            }
        }));
    }, []);

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }

        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }
        setSubscriptionState((prev) => ({
            ...prev,
            isPolling: false
        }));
    }, []);

    const handleCancel = useCallback(() => {
        stopPolling();
        navigate('/');
    }, [navigate, stopPolling]);

    const handleSuccessfulPayment = useCallback(async (orderNumber: string) => {
        stopPolling();
        setSubscriptionState((prev) => ({
            ...prev,
            processStep: 'Aktualizowanie danych...',
            isPolling: false
        }));

        localStorage.removeItem('savedInvoiceData');
        localStorage.removeItem('savedAccountData');

        const refreshSuccess = await UserActions.refreshUserToken(dispatch);

        if (refreshSuccess) {
            setSubscriptionState((prev) => ({
                ...prev,
                status: StatusDtoOrderStatusEnum.COMPLETED,
                processStep: 'Subskrypcja została aktywowana!',
                isLoading: false,
                showPurchaseStatus: true,
                currentOrder: { orderNumer: orderNumber } as OrderCreatedDto
            }));
            activeNotification(
                'Płatność potwierdzona!',
                'Twoja subskrypcja została aktywowana',
                'success'
            );

            setTimeout(async () => {
                navigate('/');
            }, 1000);
        } else {
            setSubscriptionState((prev) => ({
                ...prev,
                status: StatusDtoOrderStatusEnum.FAILED,
                processStep: 'Błąd podczas aktualizacji danych użytkownika',
                isLoading: false,
                showPurchaseStatus: true,
                error: 'Nie udało się zaktualizować danych użytkownika. Spróbuj wylogować się i zalogować ponownie.',
                currentOrder: { orderNumer: orderNumber } as OrderCreatedDto
            }));

            activeNotification(
                'Błąd aktualizacji',
                'Nie udało się zaktualizować danych użytkownika. Odśwież stronę.',
                'danger'
            );
        }
    }, [
        dispatch,
        isLoggedUser,
        navigate,
        stopPolling
    ]);

    const handleFailedPayment = useCallback((errorMessage?: string) => {
        stopPolling();
        localStorage.setItem('savedInvoiceData', JSON.stringify(subscriptionState.invoiceData));

        if (!isLoggedUser) {
            localStorage.setItem('savedAccountData', JSON.stringify(subscriptionState.accountData));
        }

        setSubscriptionState((prev) => ({
            ...prev,
            status: StatusDtoOrderStatusEnum.FAILED,
            error: errorMessage || 'Płatność nie została zrealizowana',
            isLoading: false,
            isPolling: false,
            showPurchaseStatus: true
        }));
    }, [
        subscriptionState.invoiceData,
        subscriptionState.accountData,
        isLoggedUser,
        navigate,
        stopPolling
    ]);

    const checkPendingOrder = useCallback(async () => {
        if (!isLoggedUser) return;

        const pendingOrder = await purchaseApiRequests.getPendingSubscriptionOrder();

        if (pendingOrder) {
            let processStepMessage = 'Oczekiwanie na płatność...';
            let notificationMessage = 'Masz już złożone zamówienie subskrypcji. Sprawdź jego status.';

            if (pendingOrder.orderStatus === OrderListDtoOrderStatusEnum.REGISTERED) {
                processStepMessage = 'Płatność zarejestrowana. Oczekiwanie na potwierdzenie...';
                notificationMessage = 'Twoja płatność została zarejestrowana. Oczekiwanie na potwierdzenie może potrwać nawet do 2ch dni roboczych.';
            } else if (pendingOrder.orderStatus === OrderListDtoOrderStatusEnum.PENDING) {
                processStepMessage = 'Oczekiwanie na płatność...';
                notificationMessage = 'Masz oczekujące zamówienie subskrypcji.';
            }

            setSubscriptionState((prev) => ({
                ...prev,
                showPurchaseStatus: true,
                status: pendingOrder.orderStatus as unknown as StatusDtoOrderStatusEnum,
                processStep: processStepMessage,
                currentOrder: {
                    orderNumer: pendingOrder.orderNumber,
                    orderStatus: pendingOrder.orderStatus as unknown as OrderCreatedDtoOrderStatusEnum,
                    totalPrice: pendingOrder.totalPrice
                } as OrderCreatedDto,
                isPolling: false,
                isLoading: false
            }));

            activeNotification(
                'Znaleziono oczekujące zamówienie',
                notificationMessage,
                'warning'
            );
        }
    }, [isLoggedUser]);

    useEffect(() => {
        if (isLoggedUser && !subscriptionState.showPurchaseStatus && !hasValidSubscription(user.userRole) && !user.activePackages?.length) {
            checkPendingOrder();
        }
    }, [
        isLoggedUser,
        checkPendingOrder,
        subscriptionState.showPurchaseStatus
    ]);

    const startPollingOrderStatus = useCallback((orderNumber: string) => {
        setSubscriptionState((prev) => ({
            ...prev,
            isPolling: true
        }));

        pollingIntervalRef.current = setInterval(async () => {
            const orderStatus = await purchaseApiRequests.getOrderStatus(orderNumber);

            if (orderStatus) {
                if (orderStatus.orderStatus === StatusDtoOrderStatusEnum.PAID ||
                orderStatus.orderStatus === StatusDtoOrderStatusEnum.COMPLETED) {
                    await handleSuccessfulPayment(orderNumber);

                    return;
                } else if (orderStatus.orderStatus === StatusDtoOrderStatusEnum.FAILED ||
                      orderStatus.orderStatus === StatusDtoOrderStatusEnum.CANCELLED ||
                      orderStatus.orderStatus === StatusDtoOrderStatusEnum.EXPIRED) {
                    handleFailedPayment();

                    return;
                }

                setSubscriptionState((prev) => ({
                    ...prev,
                    status: orderStatus.orderStatus
                }));
            }
        }, POLLING_INTERVAL_MS);

        pollingTimeoutRef.current = setTimeout(() => {
            stopPolling();
            activeNotification(
                'Timeout płatności',
                'Sprawdzanie statusu płatności zostało przerwane.',
                'warning'
            );
            setSubscriptionState((prev) => ({
                ...prev,
                isLoading: false,
                processStep: 'Oczekiwanie na potwierdzenie płatności. Sprawdź status ręcznie.'
            }));
        }, POLLING_TIMEOUT_MS);
    }, [
        stopPolling,
        handleSuccessfulPayment,
        handleFailedPayment
    ]);

    const handleCheckPaymentStatus = async () => {
        try {
            const refreshSuccess = await UserActions.refreshUserToken(dispatch);

            if (refreshSuccess) {
                if (hasValidSubscription(user.userRole) || user.activePackages?.length > 0) {
                    activeNotification(
                        'Płatność potwierdzona!',
                        'Twoja płatność została zrealizowana',
                        'success'
                    );
                    navigate('/');
                } else {
                    activeNotification(
                        'Status płatności',
                        'Płatność nadal nie została potwierdzona. Spróbuj ponownie za chwilę.',
                        'warning'
                    );
                }
            } else {
                activeNotification(
                    'Błąd',
                    'Nie udało się sprawdzić statusu płatności',
                    'danger'
                );
            }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            activeNotification(
                'Błąd',
                'Nie udało się sprawdzić statusu płatności',
                'danger'
            );
        }
    };

    const handleSubmitForm = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        setSubscriptionState((prev) => ({
            ...prev,
            triggerValidation: true
        }));

        const formErrors = validateForm(subscriptionState.accountData, subscriptionState.invoiceData, !isLoggedUser);

        setSubscriptionState((prev) => ({
            ...prev,
            errors: formErrors
        }));

        if (hasErrors(formErrors)) {
            activeNotification('Formularz', 'Wypełnij poprawnie wszystkie wymagane pola', 'warning');

            return;
        }

        setSubscriptionState((prev) => ({
            ...prev,
            isLoading: true,
            processStep: 'Rozpoczynanie procesu...'
        }));

        if (!isLoggedUser) {
            setSubscriptionState((prev) => ({
                ...prev,
                processStep: 'Tworzenie konta...'
            }));

            const accountCreated = await userApiRequests.createAccount(
                subscriptionState.accountData.email,
                subscriptionState.accountData.password,
                subscriptionState.accountData.confirmPassword,
                subscriptionState.invoiceData.firstName,
                subscriptionState.invoiceData.lastName,
                subscriptionState.invoiceData.companyName || '',
                subscriptionState.invoiceData.phoneNumber
            );

            if (!accountCreated) {
                setSubscriptionState((prev) => ({
                    ...prev,
                    isLoading: false
                }));

                return;
            }

            await UserActions.logInUser(dispatch, subscriptionState.accountData.email, subscriptionState.accountData.password, true);
        }

        setSubscriptionState((prev) => ({
            ...prev,
            showPurchaseStatus: true,
            isLoading: false
        }));

        setSubscriptionState((prev) => ({
            ...prev,
            processStep: 'Przygotowywanie zamówienia...'
        }));

        await purchaseApiRequests.addProductToCart(SUBSCRIPTION_PRODUCT_ID, 1);

        setSubscriptionState((prev) => ({
            ...prev,
            processStep: 'Tworzenie zamówienia...'
        }));

        const invoiceDataForOrder = {
            firstName: subscriptionState.invoiceData.firstName,
            lastName: subscriptionState.invoiceData.lastName,
            companyName: subscriptionState.invoiceData.companyName,
            taxId: subscriptionState.invoiceData.taxId,
            phoneNumber: subscriptionState.invoiceData.phoneNumber,
            street: subscriptionState.invoiceData.street,
            city: subscriptionState.invoiceData.city,
            postalCode: subscriptionState.invoiceData.postalCode
        };

        const order = await purchaseApiRequests.createOrder(invoiceDataForOrder);

        if (!order) {
            activeNotification('Błąd', 'Nie udało się utworzyć zamówienia', 'danger');
            setSubscriptionState((prev) => ({
                ...prev,
                isLoading: false
            }));

            return;
        }

        setSubscriptionState((prev) => ({
            ...prev,
            currentOrder: order
        }));

        const paymentWindow = window.open('about:blank', '_blank');

        setSubscriptionState((prev) => ({
            ...prev,
            processStep: 'Zakup...'
        }));

        const paymentUrl = await purchaseApiRequests.getPayTransactionOrder(order.orderNumer);

        if (paymentWindow) {
            createPaymentLoadingContent(paymentWindow);
        }

        if (!paymentUrl) {
            activeNotification('Błąd', 'Nie udało się utworzyć płatności', 'danger');
            setSubscriptionState((prev) => ({
                ...prev,
                isLoading: false
            }));
            paymentWindow?.close();

            return;
        }

        if (paymentWindow && !paymentWindow.closed) {
            setTimeout(() => {
                paymentWindow.location.href = paymentUrl;
            }, 1500);
        } else {
            window.location.href = paymentUrl;

            return;
        }

        setSubscriptionState((prev) => ({
            ...prev,
            processStep: 'Oczekiwanie na płatność...'
        }));

        startPollingOrderStatus(order.orderNumer);
    }, [
        subscriptionState.accountData,
        subscriptionState.invoiceData,
        isLoggedUser,
        dispatch,
        startPollingOrderStatus
    ]);

    const clearState = useCallback(() => {
        setSubscriptionState(initialState.subscriptionPurchaseInitialState());
        localStorage.removeItem('savedInvoiceData');
        localStorage.removeItem('savedAccountData');
        localStorage.removeItem('pendingProductId');
    }, []);

    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    return (
        <SubscriptionPurchaseContext.Provider
            value={{
                subscriptionState,
                handleAccountDataChange,
                handleInvoiceDataChange,
                handleSubmitForm,
                handleCancel,
                clearState,
                checkPendingOrder,
                handleCheckPaymentStatus
            }}
        >
            {children}
        </SubscriptionPurchaseContext.Provider>
    );
};
